import React, { useEffect, useState } from "react";
import { Lock, Check, X, Trash2, RefreshCw, Mail, Eye, Plus, ChevronDown, KeyRound, ShieldAlert, Ban } from "lucide-react";
import {
    adminListRestrictedRequests, adminApproveRestrictedRequest, adminDenyRestrictedRequest, adminDeleteRestrictedRequest,
    adminListRestrictedUsers, adminCreateRestrictedUser, adminResetRestrictedUserPassword, adminToggleRestrictedUser, adminDeleteRestrictedUser,
    adminListRestrictedProducts, adminPatchRestrictedProduct,
} from "@/lib/api";
import { toast } from "sonner";

const CRIMSON = "#C8102E";

export function RestrictedAdminPanel({ token }) {
    const [sub, setSub] = useState("requests"); // requests | users | products
    return (
        <div className="space-y-6" data-testid="restricted-admin-panel">
            <div className="border p-6" style={{ borderColor: "#3a1418", background: "rgba(200,16,46,0.04)" }}>
                <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4" style={{ color: CRIMSON }} strokeWidth={2} />
                    <p className="overline text-[10px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ RESTRICTED CATALOG MANAGEMENT</p>
                </div>
                <h2 className="font-display text-2xl font-bold text-ink">Members-only AAS section</h2>
                <p className="text-xs text-ink-dim mt-1.5">Approve research access requests, manage approved researcher accounts, and edit restricted product prices and stock.</p>
            </div>

            <div className="flex gap-2 flex-wrap">
                {[
                    { k: "requests", label: "Access Requests" },
                    { k: "users", label: "Approved Users" },
                    { k: "products", label: "Restricted Products" },
                ].map((t) => (
                    <button key={t.k} onClick={() => setSub(t.k)}
                        className="px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] border transition-colors"
                        style={{
                            borderColor: sub === t.k ? CRIMSON : "#1a2332",
                            color: sub === t.k ? CRIMSON : "#9aa5b4",
                            background: sub === t.k ? "rgba(200,16,46,0.08)" : "transparent",
                        }}
                        data-testid={`restricted-subtab-${t.k}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {sub === "requests" && <RequestsTab token={token} />}
            {sub === "users" && <UsersTab token={token} />}
            {sub === "products" && <ProductsTab token={token} />}
        </div>
    );
}

function RequestsTab({ token }) {
    const [requests, setRequests] = useState(null);
    const [filter, setFilter] = useState("pending");
    const [expanded, setExpanded] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => adminListRestrictedRequests(token, filter === "all" ? null : filter)
        .then((d) => setRequests(d.requests))
        .catch(() => toast.error("Failed to load requests"));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, token]);

    const approve = async (id, email) => {
        if (!window.confirm(`Approve and email credentials to ${email}?`)) return;
        setBusy(true);
        try {
            const r = await adminApproveRestrictedRequest(token, id);
            toast.success(r.duplicate ? `User already existed for ${email}` : `Approved · password emailed to ${email}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Approve failed");
        } finally { setBusy(false); }
    };
    const deny = async (id) => {
        if (!window.confirm("Mark this request as denied?")) return;
        try { await adminDenyRestrictedRequest(token, id); toast.success("Request denied"); load(); }
        catch { toast.error("Deny failed"); }
    };
    const del = async (id) => {
        if (!window.confirm("Permanently delete this request?")) return;
        try { await adminDeleteRestrictedRequest(token, id); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div className="border bg-ink-surface" style={{ borderColor: "#3a1418" }} data-testid="restricted-requests-tab">
            <div className="p-5 flex items-center justify-between gap-3 flex-wrap border-b" style={{ borderColor: "#3a1418" }}>
                <div className="flex items-center gap-2">
                    {["pending", "approved", "denied", "all"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)}
                            className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border transition-colors"
                            style={{
                                borderColor: filter === f ? CRIMSON : "#1a2332",
                                color: filter === f ? CRIMSON : "#9aa5b4",
                            }}
                            data-testid={`requests-filter-${f}`}>{f}</button>
                    ))}
                </div>
                <button onClick={load} className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-dim hover:text-ink" data-testid="requests-refresh">
                    <RefreshCw className="w-3 h-3" /> REFRESH
                </button>
            </div>

            {requests === null && <div className="p-6 text-ink-mute text-sm font-mono">LOADING…</div>}
            {requests && requests.length === 0 && (
                <div className="p-6 text-ink-mute text-sm" data-testid="requests-empty">No {filter} requests.</div>
            )}
            {requests && requests.length > 0 && (
                <table className="w-full text-sm">
                    <thead className="bg-ink-base">
                        <tr className="text-left">
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">EMAIL · USERNAME</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">COUNTRY</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">FOUND VIA</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">STATUS</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono text-right">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {requests.map((r) => (
                            <React.Fragment key={r.id}>
                                <tr className="border-t hover:bg-ink-base/40" style={{ borderColor: "#1a2332" }} data-testid={`request-row-${r.id}`}>
                                    <td className="p-3">
                                        <div className="text-ink">{r.email}</div>
                                        <div className="text-[10px] font-mono text-ink-mute">@{r.username}</div>
                                    </td>
                                    <td className="p-3 text-xs text-ink-dim">{r.country}</td>
                                    <td className="p-3 text-xs text-ink-dim">{r.how_found}</td>
                                    <td className="p-3">
                                        <span className="px-2 py-0.5 text-[9px] font-mono font-bold uppercase border"
                                            style={{
                                                borderColor: r.status === "pending" ? "#d4af37" : r.status === "approved" ? "#10b981" : "#3a1418",
                                                color: r.status === "pending" ? "#d4af37" : r.status === "approved" ? "#10b981" : CRIMSON,
                                            }}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right whitespace-nowrap space-x-2">
                                        <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                            className="text-[10px] font-mono uppercase tracking-wider text-ink-dim hover:text-ink"
                                            data-testid={`request-view-${r.id}`}><Eye className="w-3 h-3 inline" /> VIEW</button>
                                        {r.status === "pending" && (
                                            <>
                                                <button onClick={() => approve(r.id, r.email)} disabled={busy}
                                                    className="text-[10px] font-mono uppercase tracking-wider hover:underline disabled:opacity-50"
                                                    style={{ color: "#10b981" }}
                                                    data-testid={`request-approve-${r.id}`}><Check className="w-3 h-3 inline" /> APPROVE</button>
                                                <button onClick={() => deny(r.id)}
                                                    className="text-[10px] font-mono uppercase tracking-wider hover:underline"
                                                    style={{ color: CRIMSON }}
                                                    data-testid={`request-deny-${r.id}`}><X className="w-3 h-3 inline" /> DENY</button>
                                            </>
                                        )}
                                        <button onClick={() => del(r.id)} className="text-[10px] font-mono uppercase tracking-wider text-ink-mute hover:text-red-500" data-testid={`request-delete-${r.id}`}>
                                            <Trash2 className="w-3 h-3 inline" />
                                        </button>
                                    </td>
                                </tr>
                                {expanded === r.id && (
                                    <tr style={{ background: "rgba(200,16,46,0.04)" }}>
                                        <td colSpan={5} className="p-5 border-t" style={{ borderColor: "#3a1418" }} data-testid={`request-detail-${r.id}`}>
                                            <div className="grid sm:grid-cols-2 gap-5">
                                                <div>
                                                    <p className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1.5">RESEARCH EXPERIENCE</p>
                                                    <p className="text-xs text-ink leading-relaxed whitespace-pre-wrap">{r.research_experience}</p>
                                                </div>
                                                <div>
                                                    <p className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1.5">COMPOUNDS OF INTEREST</p>
                                                    <p className="text-xs text-ink leading-relaxed whitespace-pre-wrap">{r.interests}</p>
                                                </div>
                                            </div>
                                            <p className="text-[10px] font-mono text-ink-mute mt-4">Submitted {new Date(r.created_at).toLocaleString()} from {r.ip}</p>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function UsersTab({ token }) {
    const [users, setUsers] = useState(null);
    const [creating, setCreating] = useState(false);
    const [newUser, setNewUser] = useState({ email: "", username: "", send_credentials_email: true });
    const [busy, setBusy] = useState(false);
    const [generated, setGenerated] = useState(null); // {username, password, email}

    const load = () => adminListRestrictedUsers(token).then((d) => setUsers(d.users)).catch(() => toast.error("Failed to load users"));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    const create = async () => {
        if (!newUser.email || !newUser.username) { toast.error("Email and username required"); return; }
        setBusy(true);
        try {
            const r = await adminCreateRestrictedUser(token, newUser);
            toast.success(`User created · ${r.credentials_sent_to ? `credentials emailed to ${r.credentials_sent_to}` : "remember to send credentials manually"}`);
            setGenerated({ username: r.username, password: r.password, email: newUser.email, sentByEmail: !!r.credentials_sent_to });
            setNewUser({ email: "", username: "", send_credentials_email: true });
            setCreating(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed");
        } finally { setBusy(false); }
    };

    const resetPwd = async (u) => {
        if (!window.confirm(`Reset password for ${u.username}? A new password will be emailed to ${u.email}.`)) return;
        try {
            const r = await adminResetRestrictedUserPassword(token, u.id);
            setGenerated({ username: u.username, password: r.new_password, email: u.email, sentByEmail: true });
            toast.success(`Password reset, emailed to ${u.email}`);
        } catch { toast.error("Reset failed"); }
    };
    const toggle = async (u) => {
        try { await adminToggleRestrictedUser(token, u.id); toast.success(`User ${u.disabled ? "enabled" : "disabled"}`); load(); }
        catch { toast.error("Toggle failed"); }
    };
    const del = async (u) => {
        if (!window.confirm(`Permanently delete ${u.username} (${u.email})?`)) return;
        try { await adminDeleteRestrictedUser(token, u.id); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div className="space-y-4" data-testid="restricted-users-tab">
            {/* Generated password reveal */}
            {generated && (
                <div className="border-l-4 bg-ink-surface p-5 flex items-start justify-between gap-4" style={{ borderLeftColor: CRIMSON }} data-testid="generated-credentials">
                    <div>
                        <p className="overline text-[10px] tracking-[0.18em]" style={{ color: CRIMSON }}>/ CREDENTIALS GENERATED</p>
                        <p className="text-sm text-ink mt-2">Username: <span className="font-mono font-bold">{generated.username}</span></p>
                        <p className="text-sm text-ink">Password: <span className="font-mono font-bold" style={{ color: CRIMSON }}>{generated.password}</span></p>
                        <p className="text-xs text-ink-dim mt-1">{generated.sentByEmail ? `Emailed to ${generated.email}` : `Send to ${generated.email} manually — they will not see this again.`}</p>
                    </div>
                    <button onClick={() => setGenerated(null)} className="text-ink-dim hover:text-ink"><X className="w-4 h-4" /></button>
                </div>
            )}

            <div className="border bg-ink-surface" style={{ borderColor: "#3a1418" }}>
                <div className="p-5 flex items-center justify-between gap-3 flex-wrap border-b" style={{ borderColor: "#3a1418" }}>
                    <h3 className="font-display text-lg font-bold text-ink">Approved researchers · {users?.length ?? "—"}</h3>
                    <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider" style={{ background: CRIMSON, color: "#fff" }} data-testid="restricted-create-user-btn">
                        <Plus className="w-3.5 h-3.5" /> NEW USER
                    </button>
                </div>

                {users === null && <div className="p-6 text-ink-mute font-mono text-sm">LOADING…</div>}
                {users && users.length === 0 && <div className="p-6 text-ink-mute text-sm" data-testid="users-empty">No approved researchers yet.</div>}
                {users && users.length > 0 && (
                    <table className="w-full text-sm">
                        <thead className="bg-ink-base">
                            <tr className="text-left">
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">EMAIL · USERNAME</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">CREATED</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">LAST LOGIN</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">STATE</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono text-right">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-t hover:bg-ink-base/40" style={{ borderColor: "#1a2332" }} data-testid={`user-row-${u.id}`}>
                                    <td className="p-3">
                                        <div className="text-ink">{u.email}</div>
                                        <div className="text-[10px] font-mono text-ink-mute">@{u.username}</div>
                                    </td>
                                    <td className="p-3 text-[11px] font-mono text-ink-dim">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                                    <td className="p-3 text-[11px] font-mono text-ink-dim">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                                    <td className="p-3">
                                        <span className="px-2 py-0.5 text-[9px] font-mono font-bold uppercase border"
                                            style={{
                                                borderColor: u.disabled ? CRIMSON : "#10b981",
                                                color: u.disabled ? CRIMSON : "#10b981",
                                            }}>
                                            {u.disabled ? "DISABLED" : "● ACTIVE"}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right whitespace-nowrap space-x-2">
                                        <button onClick={() => resetPwd(u)} className="text-[10px] font-mono uppercase tracking-wider hover:underline" style={{ color: CRIMSON }} data-testid={`user-reset-${u.id}`}>
                                            <KeyRound className="w-3 h-3 inline" /> RESET PW
                                        </button>
                                        <button onClick={() => toggle(u)} className="text-[10px] font-mono uppercase tracking-wider text-ink-dim hover:text-ink" data-testid={`user-toggle-${u.id}`}>
                                            <Ban className="w-3 h-3 inline" /> {u.disabled ? "ENABLE" : "DISABLE"}
                                        </button>
                                        <button onClick={() => del(u)} className="text-[10px] font-mono uppercase tracking-wider text-ink-mute hover:text-red-500" data-testid={`user-delete-${u.id}`}>
                                            <Trash2 className="w-3 h-3 inline" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {creating && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setCreating(false)} data-testid="restricted-create-modal">
                    <div className="bg-ink-base border max-w-md w-full p-6" style={{ borderColor: CRIMSON }} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-4">
                            <h4 className="font-display text-xl font-bold text-ink">New researcher account</h4>
                            <button onClick={() => setCreating(false)} className="text-ink-dim hover:text-ink"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-xs text-ink-dim mb-5">A secure random password is auto-generated. Optionally email the credentials directly to the researcher.</p>
                        <label className="block mb-3">
                            <span className="overline text-[10px] tracking-[0.18em] text-ink-mute mb-1.5 block">EMAIL</span>
                            <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                className="w-full bg-ink-surface border border-[#3a1418] focus:border-[#C8102E] text-ink px-3 py-2.5 text-sm outline-none" data-testid="restricted-new-email" />
                        </label>
                        <label className="block mb-4">
                            <span className="overline text-[10px] tracking-[0.18em] text-ink-mute mb-1.5 block">USERNAME</span>
                            <input type="text" value={newUser.username}
                                onChange={(e) => setNewUser({ ...newUser, username: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "") })}
                                className="w-full bg-ink-surface border border-[#3a1418] focus:border-[#C8102E] text-ink px-3 py-2.5 text-sm outline-none" data-testid="restricted-new-username" />
                        </label>
                        <label className="flex items-center gap-2 mb-5 cursor-pointer">
                            <input type="checkbox" checked={newUser.send_credentials_email}
                                onChange={(e) => setNewUser({ ...newUser, send_credentials_email: e.target.checked })}
                                className="w-4 h-4 accent-[#C8102E]" data-testid="restricted-new-send-email" />
                            <span className="text-xs text-ink-dim">Email credentials to researcher (crimson template)</span>
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setCreating(false)} className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-[#3a1418] text-ink-dim hover:text-ink">CANCEL</button>
                            <button onClick={create} disabled={busy} className="px-5 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50" style={{ background: CRIMSON, color: "#fff" }} data-testid="restricted-new-create">
                                {busy ? "CREATING…" : "CREATE & GENERATE PASSWORD"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProductsTab({ token }) {
    const [products, setProducts] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => adminListRestrictedProducts(token).then((d) => setProducts(d.products)).catch(() => toast.error("Failed to load products"));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    const saveField = async (p, field, value) => {
        setBusy(true);
        try {
            const patch = field === "price_usd" ? { price_usd: parseFloat(value) } : { in_stock: !!value };
            await adminPatchRestrictedProduct(token, p.id, patch);
            toast.success("Updated");
            load();
        } catch { toast.error("Update failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="border bg-ink-surface" style={{ borderColor: "#3a1418" }} data-testid="restricted-products-tab">
            <div className="p-5 border-b" style={{ borderColor: "#3a1418" }}>
                <h3 className="font-display text-lg font-bold text-ink">Restricted catalog products</h3>
                <p className="text-xs text-ink-dim mt-1">Three pre-loaded AAS products. Edit price (USD) or toggle stock. Hidden from the public catalog at all times.</p>
            </div>

            {products === null && <div className="p-6 text-ink-mute font-mono text-sm">LOADING…</div>}
            {products && products.length > 0 && (
                <table className="w-full text-sm">
                    <thead className="bg-ink-base">
                        <tr className="text-left">
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">PRODUCT</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">DOSAGE</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">PRICE USD</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">STOCK</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p) => (
                            <ProductRow key={p.id} p={p} onSave={saveField} busy={busy} />
                        ))}
                    </tbody>
                </table>
            )}

            <div className="p-5 border-t flex items-start gap-3" style={{ borderColor: "#3a1418", background: "rgba(200,16,46,0.04)" }}>
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" style={{ color: CRIMSON }} strokeWidth={1.5} />
                <p className="text-[10px] font-mono uppercase tracking-[0.15em] leading-relaxed" style={{ color: CRIMSON }}>
                    STRICTLY FOR LABORATORY AND IN-VITRO RESEARCH PURPOSES ONLY. NOT FOR HUMAN OR ANIMAL CONSUMPTION.
                </p>
            </div>
        </div>
    );
}

function ProductRow({ p, onSave, busy }) {
    const [price, setPrice] = useState(p.price_usd);
    useEffect(() => { setPrice(p.price_usd); }, [p.price_usd]);
    return (
        <tr className="border-t" style={{ borderColor: "#1a2332" }} data-testid={`restricted-product-row-${p.id}`}>
            <td className="p-3">
                <div className="text-ink font-display">{p.name}</div>
                <div className="text-[10px] font-mono text-ink-mute">{p.id}</div>
            </td>
            <td className="p-3 text-xs font-mono text-ink-dim">{p.dosage}</td>
            <td className="p-3">
                <div className="inline-flex items-center gap-2">
                    <span className="text-ink-mute font-mono">$</span>
                    <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                        className="bg-ink-base border border-[#3a1418] focus:border-[#C8102E] text-ink px-2 py-1 text-sm font-mono w-24 outline-none"
                        data-testid={`restricted-price-input-${p.id}`} />
                    <button onClick={() => onSave(p, "price_usd", price)} disabled={busy || parseFloat(price) === p.price_usd}
                        className="text-[10px] font-mono uppercase tracking-wider hover:underline disabled:opacity-30"
                        style={{ color: CRIMSON }}
                        data-testid={`restricted-price-save-${p.id}`}>SAVE</button>
                </div>
            </td>
            <td className="p-3">
                <button onClick={() => onSave(p, "in_stock", !p.in_stock)} disabled={busy}
                    className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider border"
                    style={{
                        borderColor: p.in_stock ? "#10b981" : CRIMSON,
                        color: p.in_stock ? "#10b981" : CRIMSON,
                    }}
                    data-testid={`restricted-stock-toggle-${p.id}`}>
                    {p.in_stock ? "● IN STOCK" : "OUT OF STOCK"}
                </button>
            </td>
        </tr>
    );
}
